import { LOGO_URL, FAVICON_URL } from "./watermarkConfig";

export type WatermarkPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "middle-left"
  | "center"
  | "middle-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export interface ImageWatermarkOptions {
  useFullLogo?: boolean;
  position?: WatermarkPosition;
  opacity?: number;
  scale?: number;
  rotation?: number;
  quality?: number;
  pad?: number;
  logoUrl: string;
  faviconUrl: string;
}

export const DEFAULT_IMAGE_WATERMARK: ImageWatermarkOptions = {
  useFullLogo: true,
  position: "center",
  opacity: 0.36,
  scale: 0.4,
  rotation: 45,
  quality: 100,
  pad: 0,
  logoUrl: LOGO_URL,
  faviconUrl: FAVICON_URL,
};

export function getWatermarkedImgixUrl(
  originalUrl: string | undefined | null,
  options: Partial<ImageWatermarkOptions> = {},
): string {
  // Early return if no valid input or watermark disabled
  if (
    !originalUrl ||
    typeof originalUrl !== "string" ||
    originalUrl.trim() === "" ||
    process.env.NEXT_PUBLIC_ENABLE_WATERMARK_ON_IMAGES !== "true"
  ) {
    // Return a safe fallback (or original if it exists but invalid)
    return originalUrl && typeof originalUrl === "string" ? originalUrl : "";
  }

  const opts = { ...DEFAULT_IMAGE_WATERMARK, ...options };

  const markUrl = opts.useFullLogo
    ? DEFAULT_IMAGE_WATERMARK.logoUrl
    : DEFAULT_IMAGE_WATERMARK.faviconUrl;

  let baseUrl: string;

  // Handle relative paths (common in your DB) → prepend your Imgix domain
  if (
    originalUrl.startsWith("/") ||
    originalUrl.startsWith("./") ||
    !originalUrl.includes("://")
  ) {
    // Assuming all images are served via Imgix domain
    // Replace with your actual Imgix domain if different
    const imgixDomain = process.env.NEXT_PUBLIC_IMAGIX_URL;

    // Clean up leading slashes
    const cleanPath = originalUrl.replace(/^\.+\/+/, "/");
    baseUrl = `${imgixDomain}${cleanPath}`;
  } else {
    // Already absolute → use as-is
    baseUrl = originalUrl;
  }

  try {
    const url = new URL(baseUrl);
    const existingParams = Object.fromEntries(url.searchParams.entries());
    url.search = "";

    let markAlign = "center";

    switch (opts.position) {
      case "top-left":
        markAlign = "top,left";
        break;
      case "top-center":
        markAlign = "top,center";
        break;
      case "top-right":
        markAlign = "top,right";
        break;
      case "middle-left":
        markAlign = "middle,left";
        break;
      case "center":
        markAlign = "middle,center";
        break;
      case "middle-right":
        markAlign = "middle,right";
        break;
      case "bottom-left":
        markAlign = "bottom,left";
        break;
      case "bottom-center":
        markAlign = "bottom,center";
        break;
      case "bottom-right":
        markAlign = "bottom,right";
        break;
      default:
        markAlign = "bottom,right";
    }

    const paramsObj: Record<string, string> = {
      ...existingParams,
      mark: markUrl,
      "mark-align": markAlign,
      "mark-pad": typeof opts.pad === "number" ? opts.pad.toString() : "0",
      auto: "compress,format",
      q: typeof opts.quality === "number" ? opts.quality.toString() : "100",
    };

    if (typeof opts.opacity === "number" && !Number.isNaN(opts.opacity)) {
      paramsObj["mark-alpha"] = Math.round(opts.opacity * 100).toString();
    }

    if (typeof opts.scale === "number" && !Number.isNaN(opts.scale)) {
      paramsObj["mark-scale"] = Math.round(opts.scale * 100).toString();
    }

    if (typeof opts.rotation === "number" && !Number.isNaN(opts.rotation)) {
      paramsObj["mark-rot"] = Math.round(opts.rotation).toString();
    }

    const searchParams = new URLSearchParams(paramsObj);
    url.search = searchParams.toString();

    return url.toString();
  } catch (err) {
    console.warn(
      `[getWatermarkedImgixUrl] Invalid URL input: "${originalUrl}" → returning original`,
      err,
    );
    return baseUrl; // fallback to non-watermarked if parsing still fails
  }
}
